/**
 * Media mapping — converts between the page-builder's block/form model and the
 * backend's media request/response shapes.
 *
 *   blocksToMedia            — form blocks  → API request media (create & edit)
 *   mediaToBlocks            — API response media → form blocks (edit pre-fill)
 *   productResponseToFormData — full product → page-builder form data (edit)
 *
 * Image keys vs URLs: create/edit requests reference images by their upload
 * *key* (logoUrl, imageKey, imageKeys, previewImages) — a relative storage path
 * like `products/logos/<id>.webp`. The GET response returns keys for `image`
 * media and preview images, but only display *URLs* for the logo and gallery
 * images. For those two, `toStorageKey` derives the key from the display URL so
 * an unchanged image still round-trips as a path on save (the backend expects a
 * key, not a URL); the display URL is kept separately for rendering the editor.
 */

import {
  DEFAULT_VIDEO_ASPECT_RATIO,
  parseVideoUrl,
  type ContentBlock,
  type TextBlock,
  type ImageBlock,
  type VideoBlock,
  type GalleryBlock,
  type PageBuilderFormData,
} from '@/lib/types/page-builder';
import {
  STATUS_TAG_OPTIONS,
  type MediaItemRequest,
  type StatusTag,
} from '@/lib/validations/product-submission';
import type { MediaItemResponse, ProductResponseApi } from '@/lib/services/productService';

// ============================================================================
// STORAGE KEY
// ============================================================================

/**
 * Storage key for an image the API returned as a display URL.
 *
 * Create/edit requests reference images by their storage *key* — a relative
 * path like `products/logos/<id>.webp`. The GET response, however, exposes only
 * full display URLs for the logo and gallery images (no key), and those URLs
 * carry an image-processing suffix (`products/logos/<id>.webp@webp`). Convert
 * back to the bare key — strip the origin/bucket and the `@…` transform — so an
 * unchanged image round-trips as a path, matching what the backend stores for
 * `image` media and preview images. Values that are already keys (fresh
 * uploads, bare paths) pass through untouched.
 */
export function toStorageKey(value: string): string {
  if (!value) return value;

  // For a display URL, work on the pathname (drops origin, userinfo, query);
  // a bare key is used as-is.
  let path = value;
  if (/^https?:\/\//i.test(value)) {
    try {
      path = new URL(value).pathname;
    } catch {
      return value;
    }
  }

  // Drop any `@`-delimited transform directive (e.g. `.webp@webp`, `@800w`).
  const at = path.indexOf('@');
  if (at !== -1) path = path.slice(0, at);

  // Keep from `products/` on — strips the origin/bucket segments.
  const marker = path.indexOf('/products/');
  return marker !== -1 ? path.slice(marker + 1) : path.replace(/^\/+/, '');
}

// ============================================================================
// FORWARD: blocks → media request
// ============================================================================

/** Convert page-builder blocks to the API's media array (new API format). */
export function blocksToMedia(blocks: ContentBlock[]): MediaItemRequest[] {
  return blocks.flatMap((block): MediaItemRequest[] => {
    switch (block.type) {
      case 'text':
        // Use plainText for markdown content
        if (!block.content.plainText.trim()) return [];
        return [{ type: 'text', content: block.content.plainText }];

      case 'image':
        if (!block.content.image) return [];
        return [{ type: 'image', imageKey: block.content.image.key }];

      case 'video': {
        // Custom embeds are sent as 'iframe', standard video URLs as 'video'.
        const layout = {
          scalePercent: block.content.scalePercent ?? 100,
          offsetX: block.content.offsetX ?? 0,
          offsetY: block.content.offsetY ?? 0,
        };
        if (block.content.embedSrc) {
          return [{
            type: 'iframe',
            src: block.content.embedSrc,
            aspectRatio: block.content.aspectRatio ?? DEFAULT_VIDEO_ASPECT_RATIO,
            ...layout,
          }];
        }
        if (!block.content.url) return [];
        return [{ type: 'video', url: block.content.url, ...layout }];
      }

      case 'gallery':
        if (block.content.images.length === 0) return [];
        return [{
          type: 'gallery' as const,
          imageKeys: block.content.images.map((img) => img.key),
        }];

      default:
        return [];
    }
  });
}

// ============================================================================
// REVERSE: media response → blocks
// ============================================================================

function baseFields(index: number): { id: string; createdAt: number; updatedAt: number } {
  const now = Date.now();
  return {
    id: `block_${now}_${index}_${Math.random().toString(36).slice(2, 9)}`,
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Normalize a numeric embed-layout field coming back from the API.
 *
 * The backend echoes unset layout fields (scalePercent/offsetX/offsetY, and
 * sometimes aspectRatio) as explicit `null`, but the form model and Zod schema
 * expect `number | undefined` — `z.number().optional()` rejects `null`. Left
 * as-is, an edited iframe/video block fails validation and blocks save with no
 * visible error. Coerce any non-finite value to `undefined` so it round-trips.
 */
function numOrUndef(value: number | null | undefined): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** Convert the API's media response back into editable page-builder blocks. */
export function mediaToBlocks(media: MediaItemResponse[] | null): ContentBlock[] {
  if (!media) return [];

  return media
    .map((item, index): ContentBlock | null => {
      switch (item.type) {
        case 'text': {
          // The editor renders `html` as innerHTML; only plainText is persisted
          // server-side, so both fields mirror the stored content.
          const block: TextBlock = {
            ...baseFields(index),
            type: 'text',
            content: { html: item.content, plainText: item.content },
            style: { variant: 'paragraph' },
          };
          return block;
        }

        case 'image': {
          const block: ImageBlock = {
            ...baseFields(index),
            type: 'image',
            content: {
              image: {
                key: item.imageKey,
                originalUrl: item.imageUrl,
                thumbnailUrl: item.thumbnailUrl ?? item.imageUrl,
              },
              caption: '',
              alt: '',
            },
            style: { size: 'large', alignment: 'center' },
          };
          return block;
        }

        case 'gallery': {
          // Response gives display URLs only (no keys). Derive the storage key
          // from the URL so an unchanged gallery round-trips as a path on save,
          // while originalUrl keeps the display URL.
          const block: GalleryBlock = {
            ...baseFields(index),
            type: 'gallery',
            content: {
              images: item.images.map((img) => ({
                key: toStorageKey(img.imageUrl),
                originalUrl: img.imageUrl,
                thumbnailUrl: img.thumbnailUrl ?? img.imageUrl,
              })),
              captions: [],
            },
            style: { columns: 2 },
          };
          return block;
        }

        case 'iframe': {
          const block: VideoBlock = {
            ...baseFields(index),
            type: 'video',
            content: {
              url: '',
              embedId: '',
              provider: 'youtube',
              caption: '',
              embedSrc: item.src,
              inputMode: 'iframe',
              // aspectRatio is `positive()` (not optional) in the schema, so a
              // null/0 from the backend must fall back to the default, not pass
              // through. The layout fields are optional — null → undefined.
              aspectRatio: numOrUndef(item.aspectRatio) ?? DEFAULT_VIDEO_ASPECT_RATIO,
              scalePercent: numOrUndef(item.scalePercent),
              offsetX: numOrUndef(item.offsetX),
              offsetY: numOrUndef(item.offsetY),
            },
          };
          return block;
        }

        case 'video': {
          const parsed = parseVideoUrl(item.url);
          const block: VideoBlock = {
            ...baseFields(index),
            type: 'video',
            content: {
              url: item.url,
              embedId: parsed?.embedId ?? '',
              provider: parsed?.provider ?? 'youtube',
              caption: '',
              inputMode: 'url',
              scalePercent: numOrUndef(item.scalePercent),
              offsetX: numOrUndef(item.offsetX),
              offsetY: numOrUndef(item.offsetY),
            },
          };
          return block;
        }

        default:
          return null;
      }
    })
    .filter((block): block is ContentBlock => block !== null);
}

// ============================================================================
// REVERSE: full product → form data
// ============================================================================

const VALID_STATUS_TAGS = new Set<string>(STATUS_TAG_OPTIONS.map((o) => o.value));

/**
 * Map a product fetched from GET /products/{slug} into page-builder form data,
 * ready to pre-fill the editor. Immutable fields (slug, name) are carried
 * through so they display; the edit request drops them.
 */
export function productResponseToFormData(product: ProductResponseApi): PageBuilderFormData {
  return {
    header: {
      slug: product.slug,
      name: product.name,
      tagline: product.tagline,
      description: product.description,
      websiteUrl: product.websiteUrl ?? '',
      logo: {
        // Submit reference is the storage key; originalUrl stays the display URL.
        key: toStorageKey(product.logoUrl),
        originalUrl: product.logoUrl,
        thumbnailUrl: product.logoUrl,
      },
    },
    blocks: mediaToBlocks(product.media),
    metadata: {
      categoryIds: product.categories.map((c) => c.id),
      statusTags: (product.statusTags ?? []).filter(
        (tag): tag is StatusTag => VALID_STATUS_TAGS.has(tag)
      ),
      previewImages: product.previewImages ?? [],
      pendingInvitations: [],
      launchDate: product.launchDate ? product.launchDate.slice(0, 10) : undefined,
    },
  };
}
